import {
  DealAction,
  DealStatus,
  type DealDispatcher,
} from '@pelando/components';
import { useRouter } from 'next/router';
import { useTranslation } from '@/presentation/hooks/useTranslation';
import { getTimeAgoString } from '@/presentation/services/time/getTimeAgo';
import { type VoteVariables } from '@/infra/api/votes/types/Vote';
import { type RemoveVoteVariables } from '@/infra/api/votes/types/RemoveVote';
import getStampType from '@/presentation/services/deals/getStampType';
import getDealValue from '@/presentation/services/deals/getDealValue';
import {
  getDealUrl,
  getCouponsUrl,
  getCommentsSectionDealUrl,
} from '@/presentation/services/urls';
import { type ProfileComment } from '@/domain/entities/Comment';
import useDealHeaderActionHandler from '@/presentation/components/DealHeader/hooks/useDealHeaderActionHandler';
import actionHandlerClickouts from '../actionHandlerClickouts';
import { actionHandlerMutations } from '../actionHandlerMutations';
import { Card, ProfileCommentCardSkeleton } from './styles';

export { ProfileCommentCardSkeleton };

type ProfileCommentCardProps = {
  comment: ProfileComment;
  onVote: (variables: VoteVariables) => void;
  onRemoveVote: (variables: RemoveVoteVariables) => void;
  cardPosition: number;
  userNickname: string;
  isVerified: boolean;
  userImage?: string;
};

export const ProfileCommentCard = ({
  comment,
  onVote,
  onRemoveVote,
  cardPosition,
  isVerified,
  userNickname,
  userImage = '',
}: ProfileCommentCardProps) => {
  const { push, locale = '' } = useRouter();
  const { t } = useTranslation('common', 'profile');

  const storeLabel = t('store-label');

  const { offer: deal, content } = comment;

  const {
    status,
    kind,
    store,
    id,
    title,
    timestamps,
    image,
    commentCount,
    voteInfo,
    price,
  } = deal;

  const imageUrl = image?.url || '';
  const storeImageUrl = store?.image.url || '';
  const storeSlug = store?.slug || '';
  const storeName = store?.name || '';
  const commentId = comment.id;
  const temperature = Math.round(deal.temperature);
  const value = getDealValue(deal);
  const stamp = getStampType(deal);
  const dealUrl = getDealUrl({
    id,
    title,
  });
  const commentsDealUrl = getCommentsSectionDealUrl(dealUrl);
  const storeUrl = getCouponsUrl({
    storeSlug,
    locale,
  });
  const timestamp = getTimeAgoString(
    new Date(timestamps?.createdAt || ''),
    locale
  );

  const actionHandler = useDealHeaderActionHandler({
    deal: {
      id,
      title,
      kind,
      status,
      price,
    },
  });

  const actionsHandler: DealDispatcher = ({ type, payload }) => {
    actionHandler(type as DealAction);
    actionHandlerMutations({ action: { type, payload }, onVote, onRemoveVote });
    actionHandlerClickouts({
      action: { type, payload },
      push,
      kind,
      status,
      dealId: id,
      storeName,
      dealUrl,
      cardPosition,
      locale,
    });
  };

  return (
    <Card
      dealUrl={dealUrl}
      commentsDealUrl={commentsDealUrl}
      id={id}
      key={commentId}
      authorNickname={userNickname}
      isAuthorVerified={isVerified}
      commentCount={commentCount}
      commentId={commentId}
      content={content}
      dispatch={actionsHandler}
      status={status as unknown as DealStatus}
      temperature={temperature}
      title={title}
      authorImage={userImage}
      image={imageUrl}
      storeImage={storeImageUrl}
      timestamp={timestamp}
      storeLink={storeUrl}
      value={value}
      stampType={stamp}
      storeSlug={storeSlug}
      storeName={storeName}
      voteInfo={voteInfo}
      storeText={storeLabel}
      locale={locale}
    />
  );
};
